import React, { useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"

import { isNumber } from "../../utils"

import {
  isLTPM,
  EmailResultsInputElement,
  withApplicationConfig,
} from "../../../components/shared_components_registration"
import { getExcelTravelerFile, getReactorExcelTemplate } from "../../../Loaders"
import { Required } from "../../widgets/utils"

const registration = {
  export_excel_label: (intl) =>
    isLTPM ? intl.formatMessage({ id: "general.export.excel.traveler" }) : "Export Excel Traveler",
  fill_out_all_fields: (intl) =>
    isLTPM ? intl.formatMessage({ id: "general.required.form.fields.empty" }) : "Please fill out all fields",
  resampling_window_min_label: (intl) =>
    isLTPM
      ? intl.formatMessage({ id: "general.resampling.data.window.minutes" })
      : "Resampling Data Window (Minutes)",
  tip_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.tip" }) : "Tip"),
  submit_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.submitButton" }) : "Submit"),
  close_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.closeButton" }) : "Close"),
  can_email: !isLTPM,
}

function OutputExcelTravelerModal({
  show,
  onHide,
  run,
  setDownloadStatusMessage,
  setShowDownloadStatusModal,
  config = {}, // From withApplicationConfig - Currently only used for LTPM
  intl,
}) {
  const [allowTravellerGeneration, setAllowTravellerGeneration] = useState(true)
  useEffect(() => {
    if (isLTPM && config?.user?.permissions) {
      // Handle use case on LTPM where certain user types shouldn't be able to download a run excel traveller
      setAllowTravellerGeneration(config.user.permissions.includes("can_generate_excel_traveller_export"))
    }
  }, [config])

  const tipStyle = { color: "var(--lanzalake-blue-mid)", margin: "0" }

  const [emailResults, setEmailResults] = useState(registration.can_email ? "yes" : "no")
  const [dateFormat, setDateFormat] = useState("standard")
  const [resample, setResample] = useState(0)
  const [hours, setHours] = useState(0)
  const [validationErr, setValidationErr] = useState(false)

  const submitForm = (e) => {
    e.preventDefault()

    if (!isNumber(resample) || !isNumber(hours)) {
      setValidationErr(true)
    } else {
      const params = {
        email_results: emailResults,
        run: run.name,
        reactor: run.reactor,
        site: run.site || "", // LanzaLake specific
        naming_site: run.naming_site || "", // LanzaLake specific
        time_format: dateFormat,
        resample: resample,
        hours: hours,
        process_offline: true,
      }
      if (isLTPM) {
        getExcelTravelerFile(params, intl).then((_) => {})
      } else {
        getExcelTravelerFile(params).then((resp) => {
          setDownloadStatusMessage(resp?.message ? resp.message : "")
          setShowDownloadStatusModal(true)
        })
      }
      onHide()
    }
  }

  const updateInputValue = (e, updateValue) => {
    e.preventDefault()
    if (e.target.value) {
      updateValue(Math.round(e.target.value))
    } else {
      updateValue(e.target.value)
    }

    setValidationErr(false)
  }

  return (
    <Modal
      size={"lg"}
      dialogClassName={"definitions-modal"}
      show={show}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
    >
      <Modal.Header closeButton>
        <Modal.Title>{`${registration.export_excel_label(intl)}: ${run.name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {validationErr && (
            <div style={{ marginBottom: "15px" }}>
              <span className={"required-message"}>{registration.fill_out_all_fields(intl)}</span>
            </div>
          )}
          <div className="row" style={{ marginBottom: "15px" }}>
            <div className="col-6">
              <div className={"one-rem-margin-bottom"}>
                <label>
                  {registration.resampling_window_min_label(intl)}
                  <Required /> :
                </label>
                <input
                  type={"number"}
                  className={"form-control form-control-sm"}
                  onWheel={(e) => e.target.blur()}
                  step={"1"}
                  min={"0"}
                  style={{ width: "100%" }}
                  value={resample}
                  onChange={(e) => updateInputValue(e, setResample)}
                />
              </div>
              <div>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Use 0 to return raw unbinned data."}
                </p>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Using numbers larger than 0 will result in the data being resampled. For example " +
                    "a value of 30 will return data for all tags binned and averaged into 30 minute chunks."}
                </p>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Data presented with a SampleID in file was not subject to resampling."}
                </p>
              </div>
            </div>
            <div className="col-6">
              <div className={"one-rem-margin-bottom"}>
                <label>
                  {/*TODO - Translations*/}
                  Hours of Data to Return
                  <Required /> :
                </label>
                <input
                  type={"number"}
                  className={"form-control form-control-sm"}
                  onWheel={(e) => e.target.blur()}
                  step={"1"}
                  min={"0"}
                  style={{ width: "100%" }}
                  value={hours}
                  onChange={(e) => updateInputValue(e, setHours)}
                />
              </div>
              <div>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Use 0 to return all data for a run"}
                </p>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Using numbers larger than 0 will result in outputting the most recent hour(s) of data. " +
                    "For example a value of 120 would output the latest 120 hours (or 5 days) worth of data " +
                    "relative to either run's end or the max timestamp on record for the given run."}
                </p>
              </div>
            </div>
          </div>
          <div className="row" style={{ marginBottom: "15px" }}>
            <div className="col-6">
              <div className={"one-rem-margin-bottom"}>
                <label>
                  {/*TODO - Translations*/}
                  Download Date Format
                  <Required /> :
                </label>
                <select
                  name={"dateFormat"}
                  className={"form-select form-select-sm"}
                  required
                  value={dateFormat}
                  onChange={(e) => {
                    e.preventDefault()
                    setDateFormat(e.target.value)
                  }}
                >
                  <option key={"standard"} value={"standard"}>
                    {/*TODO - Translations*/}
                    Standard (YYYY-MM-DD HH:mm:ss)
                  </option>
                  <option key={"excel"} value={"excel"}>
                    {/*TODO - Translations*/}
                    Excel (e.g. 44722.38)
                  </option>
                </select>
              </div>
              <div>
                <p className="form-text" style={tipStyle}>
                  {/*TODO - Translations*/}
                  {registration.tip_label(intl)}:&nbsp;
                  {"Dates in files are local to the site timezone where the data was collected."}
                </p>
              </div>
            </div>
            <div className="col-6">
              {registration.can_email && (
                <>
                  <EmailResultsInputElement
                    value={emailResults}
                    onValueChange={(e) => {
                      e.preventDefault()
                      setEmailResults(e.target.value)
                    }}
                    disabled={true} // If emailing is possible, force queuing and don't allow processing on main application server
                  />
                  <div>
                    <p className="form-text" style={tipStyle}>
                      {/*TODO - Translations*/}
                      {registration.tip_label(intl)}:&nbsp;
                      {"Choosing 'Yes' will result in the generated file being emailed to the account associated " +
                        "with your LanzaLake login. Emailing can be helpful if the resulting file will be large and you " +
                        "don't want to wait on the browser response."}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          {run.hasExcel && ( // Excel template is available for given site/reactor
            <div className="row" style={{ marginBottom: "5px" }}>
              <div className="col-6">
                <div className={"one-rem-margin-bottom"}>
                  <label>
                    {/*TODO - Translations*/}
                    {`${run.reactor} Excel Graph Template`}:
                  </label>
                  &nbsp;
                  <Button
                    variant={"outline-primary"}
                    onClick={() =>
                      getReactorExcelTemplate(
                        isLTPM
                          ? { reactor: run.reactor }
                          : {
                              reactor: run.reactor,
                              site: run.site || "",
                              naming_site: run.naming_site || "",
                              file_id: run.fileID || "",
                            },
                      )
                    }
                  >
                    <span className={"fa fa-file-excel-o"} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={onHide}>
          {registration.close_label(intl)}
        </Button>
        {allowTravellerGeneration && (
          <Button variant={"primary"} onClick={(e) => submitForm(e)}>
            {registration.submit_label(intl)}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default withApplicationConfig(OutputExcelTravelerModal)
